import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, Tab, Textarea } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import ReactMarkdown from 'react-markdown';
import {
  type Control,
  type FieldError,
  type Path,
  Controller,
} from 'react-hook-form';
import { CtyButton, CtyShowToast, checkType, markdownPreviewStyles } from '@circuitry-ai/doc-ui';
import type { EditAgentFormValues } from './voiceAgentEditFormTypes';
import { PromptSectionSidebar } from './PromptSectionSidebar';
import {
  type PromptSection,
  SYSTEM_PROMPT_MODAL_CONTENT_HEIGHT_PX,
  getTextareaScrollElement,
  parsePromptSections,
  resolveNativeTextarea,
  scrollContainerToElement,
  scrollTextareaToLine,
  splitMarkdownBySections,
  systemPromptEditorStyles,
} from './systemPromptEditorUtils';

type PromptDirection = 'inbound' | 'outbound';
type PromptViewMode = 'edit' | 'preview';

const AXIS_TAB_CLASS_NAMES = {
  tabList: 'gap-1',
  tab: 'h-8 px-3',
  cursor: 'bg-primary dark:bg-primary',
  tabContent: 'group-data-[selected=true]:text-primary-foreground text-sm',
};

type SystemPromptInlineSectionProps = Readonly<{
  control: Control<EditAgentFormValues>;
  inboundFieldName: Path<EditAgentFormValues>;
  outboundFieldName: Path<EditAgentFormValues>;
  inboundError: FieldError | undefined;
  outboundError: FieldError | undefined;
  placeholder: string;
}>;

type SystemPromptInlineEditorProps = Readonly<{
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  errorField: { message?: string } | undefined;
  placeholder: string;
  promptDirection: PromptDirection;
  onPromptDirectionChange: (direction: PromptDirection) => void;
  viewMode: PromptViewMode;
  onViewModeChange: (mode: PromptViewMode) => void;
}>;

function SystemPromptInlineEditor({
  value,
  onChange,
  onBlur,
  errorField,
  placeholder,
  promptDirection,
  onPromptDirectionChange,
  viewMode,
  onViewModeChange,
}: SystemPromptInlineEditorProps) {
  const { t } = useTranslation();
  const [activeSectionId, setActiveSectionId] = useState<string | null>(
    () => parsePromptSections(value)[0]?.id ?? null
  );
  const [isCopied, setIsCopied] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingScrollSectionRef = useRef<PromptSection | null>(null);

  const sections = useMemo(() => parsePromptSections(value), [value]);
  const previewChunks = useMemo(
    () => splitMarkdownBySections(value, sections),
    [value, sections]
  );

  const valueRef = useRef(value);
  valueRef.current = value;

  const scrollContentToTop = useCallback(() => {
    contentContainerRef.current?.scrollTo({ top: 0 });
    const textarea = textareaRef.current;
    if (textarea) {
      getTextareaScrollElement(textarea).scrollTo({ top: 0 });
    }
    previewRef.current?.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const firstSection = parsePromptSections(valueRef.current)[0];
    setActiveSectionId(firstSection?.id ?? null);
    scrollContentToTop();
  }, [promptDirection, viewMode, scrollContentToTop]);

  const scrollPreviewToSection = useCallback((section: PromptSection) => {
    const container = previewRef.current;
    if (!container) return;

    const target = container.querySelector(`#${CSS.escape(section.id)}`);
    if (target instanceof HTMLElement) {
      scrollContainerToElement(container, target);
      return;
    }

    pendingScrollSectionRef.current = section;
  }, []);

  useEffect(() => {
    const pending = pendingScrollSectionRef.current;
    if (!pending || viewMode !== 'preview') return;

    const container = previewRef.current;
    const target = container?.querySelector(`#${CSS.escape(pending.id)}`);
    if (container && target instanceof HTMLElement) {
      scrollContainerToElement(container, target);
      pendingScrollSectionRef.current = null;
    }
  }, [viewMode, previewChunks]);

  const scrollEditToSection = useCallback((section: PromptSection) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      pendingScrollSectionRef.current = section;
      return;
    }
    scrollTextareaToLine(textarea, section.lineIndex);
  }, []);

  useEffect(() => {
    const pending = pendingScrollSectionRef.current;
    if (!pending || viewMode !== 'edit') return;

    const textarea = textareaRef.current;
    if (textarea) {
      scrollTextareaToLine(textarea, pending.lineIndex);
      pendingScrollSectionRef.current = null;
    }
  }, [viewMode, value]);

  const handleSectionSelect = useCallback(
    (section: PromptSection) => {
      setActiveSectionId(section.id);
      if (viewMode === 'preview') {
        scrollPreviewToSection(section);
        return;
      }
      scrollEditToSection(section);
    },
    [viewMode, scrollPreviewToSection, scrollEditToSection]
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      CtyShowToast(checkType.success, t('Copied to clipboard'));
      globalThis.setTimeout(() => setIsCopied(false), 2000);
    } catch {
      CtyShowToast(checkType.error, t('Failed to copy'));
    }
  }, [value, t]);

  return (
    <div className="flex flex-col gap-4">
      <style>{markdownPreviewStyles}</style>
      <style>{systemPromptEditorStyles}</style>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          size="sm"
          variant="light"
          aria-label={t('System prompt direction')}
          selectedKey={promptDirection}
          onSelectionChange={(key) => onPromptDirectionChange(key as PromptDirection)}
          classNames={AXIS_TAB_CLASS_NAMES}
        >
          <Tab key="inbound" title={t('Inbound')} />
          <Tab key="outbound" title={t('Outbound')} />
        </Tabs>
        <div className="flex items-center gap-2">
        <CtyButton
            size="sm"
            variant="light"
            aria-label={t('Copy system prompt')}
            startContent={<Icon icon="mdi:content-copy" width={16} />}
            onPress={handleCopy}
          >
            {isCopied ? t('Copied') : t('Copy')}
          </CtyButton>
          <Tabs
            size="sm"
            variant="light"
            aria-label={t('System prompt view mode')}
            selectedKey={viewMode}
            onSelectionChange={(key) => onViewModeChange(key as PromptViewMode)}
            classNames={AXIS_TAB_CLASS_NAMES}
          >
            <Tab key="preview" title={t('Preview')} />
            <Tab key="edit" title={t('Edit')} />
          </Tabs>
         
        </div>
      </div>
      <div className="flex gap-4 min-w-0">
        {sections.length > 0 ? (
          <PromptSectionSidebar
            sections={sections}
            activeSectionId={activeSectionId}
            onSelect={handleSectionSelect}
          />
        ) : null}
        <div ref={contentContainerRef} className="flex-1 min-w-0">
          {viewMode === 'edit' ? (
            <Textarea
              ref={(node) => {
                textareaRef.current = resolveNativeTextarea(node);
              }}
              placeholder={placeholder}
              value={value}
              onValueChange={onChange}
              onBlur={onBlur}
              variant="bordered"
              isInvalid={!!errorField}
              className="flex-1 min-w-0 system-prompt-editor-textarea"
            />
          ) : (
            <div
              ref={previewRef}
              className="system-prompt-preview-panel markdown-preview overflow-y-auto rounded-medium border border-divider bg-default-50 p-4"
              style={{
                height: SYSTEM_PROMPT_MODAL_CONTENT_HEIGHT_PX,
                fontSize: '14px',
                lineHeight: '1.5',
              }}
            >
              {value.trim() ? (
                previewChunks.map((chunk, index) => (
                  <div
                    key={chunk.section?.id ?? `prompt-chunk-${index}`}
                    id={chunk.section?.id}
                    className={chunk.section ? 'scroll-mt-1' : undefined}
                  >
                    <ReactMarkdown>{chunk.content}</ReactMarkdown>
                  </div>
                ))
              ) : (
                <p className="text-default-400 text-sm">{placeholder}</p>
              )}
            </div>
          )}
        </div>
      </div>
      {errorField?.message ? (
        <p className="text-tiny text-danger">{errorField.message}</p>
      ) : null}
    </div>
  );
}

export function SystemPromptInlineSection({
  control,
  inboundFieldName,
  outboundFieldName,
  inboundError,
  outboundError,
  placeholder,
}: SystemPromptInlineSectionProps) {
  const [promptDirection, setPromptDirection] = useState<PromptDirection>('inbound');
  const [viewMode, setViewMode] = useState<PromptViewMode>('preview');

  const activeFieldName = promptDirection === 'inbound' ? inboundFieldName : outboundFieldName;
  const activeError = promptDirection === 'inbound' ? inboundError : outboundError;

  return (
    <Controller
      key={activeFieldName}
      name={activeFieldName}
      control={control}
      render={({ field }) => (
        <SystemPromptInlineEditor
          key={activeFieldName}
          value={typeof field.value === 'string' ? field.value : ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          errorField={activeError}
          placeholder={placeholder}
          promptDirection={promptDirection}
          onPromptDirectionChange={setPromptDirection}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      )}
    />
  );
}
